import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import ScrollToTop from "./components/ScrollToTop";
// Necesar pentru redirecturile de la URL-urile vechi de servicii: ele duc la
// /#cap-web și celelalte ancore, iar React Router nu derulează singur la hash.
import HashScrollHandler from "./components/HashScrollHandler";
import RootErrorBoundary from "./components/RootErrorBoundary";
import Shell from "./site/Shell";
// Landing-ul rămâne eager: e punctul de intrare și elementul LCP. Paginile de
// caz sunt lazy — fiecare aduce doar marcajul ei, iar motorul 3D e un chunk
// separat, cerut abia când se deschide primul caz.
import { CASE_SLUGS } from "@/site/caseSlugs";
import { SERVICE_SLUGS } from "@/site/serviceSlugs";
import { BLOG_SLUGS } from "@/site/blog";
import { NICHE_SLUGS } from "@/site/niches";
import Home from "./pages/Home.tsx";

// O singură pagină de caz pentru toate patru: structura era identică, iar cu trei
// limbi ar fi devenit douăsprezece copii ale aceluiași marcaj.
const CasePage = lazy(() => import("./site/CasePage.tsx"));
const ServicePage = lazy(() => import("./site/ServicePage.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Tool = lazy(() => import("./pages/Tool.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./site/BlogPost.tsx"));
const NichePage = lazy(() => import("./site/NichePage.tsx"));
const Industries = lazy(() => import("./pages/Industries.tsx"));
const Work = lazy(() => import("./pages/Work.tsx"));
const Services = lazy(() => import("./pages/Services.tsx"));
const FaqPage = lazy(() => import("./pages/FaqPage.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

const CASES = CASE_SLUGS;

const RouteFallback = () => (
  <div style={{ minHeight: "100vh", background: "var(--bg)" }} aria-hidden="true" />
);

const App = () => (
  <RootErrorBoundary>
    {/* Toaster, Sonner și TooltipProvider erau montate aici din generatorul de
        proiect. Niciun component nu chema vreodată toast() și nu exista niciun
        <Tooltip> în pagină, deci trăgeau sonner și două pachete Radix în bundle
        ca să randeze trei containere goale. Scoase. */}
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <ScrollToTop />
        <HashScrollHandler />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route element={<Shell />}>
              {/* Același set de pagini, montat o dată fără prefix (engleza) și
                  o dată sub fiecare limbă. Engleza rămâne la rădăcină fiindcă
                  adresele existente sunt indexate și date clienților: mutarea
                  lor sub /en ar cere un redirect pentru fiecare și ar rupe
                  orice link partajat până se propagă. */}
              {["", "/ro", "/ru"].map((pre) => (
                <Route key={pre || "en"}>
                  <Route path={pre === "" ? "/" : pre} element={<Home />} />
                  <Route path={`${pre}/work`} element={<Work />} />
                  <Route path={`${pre}/services`} element={<Services />} />
                  <Route path={`${pre}/faq`} element={<FaqPage />} />
                  <Route path={`${pre}/contact`} element={<Contact />} />
                  {CASES.map((slug) => (
                    <Route
                      key={slug}
                      path={`${pre}/work/${slug}`}
                      element={<CasePage slug={slug} />}
                    />
                  ))}
                  {SERVICE_SLUGS.map((slug) => (
                    <Route
                      key={slug}
                      path={`${pre}/services/${slug}`}
                      element={<ServicePage slug={slug} />}
                    />
                  ))}
                  <Route path={`${pre}/blog`} element={<Blog />} />
                  {BLOG_SLUGS.map((slug) => (
                    <Route key={slug} path={`${pre}/blog/${slug}`} element={<BlogPost slug={slug} />} />
                  ))}
                  {/* Indexul lor. Stă înaintea rutei generice de nișă fiindcă
                      „industries" e rezervat în scripts/niches.mjs — nicio nișă
                      nu poate lua slugul ăsta, deci nu există ambiguitate. */}
                  <Route path={`${pre}/industries`} element={<Industries />} />
                  {/* Nișele stau la rădăcina limbii — /ro/stomatologie, nu
                      /ro/solutii/stomatologie. Nu se pot ciocni cu rutele fixe
                      de mai sus: compilatorul cade dacă un slug de nișă e unul
                      dintre ele. */}
                  {NICHE_SLUGS.map((slug) => (
                    <Route key={slug} path={`${pre}/${slug}`} element={<NichePage slug={slug} />} />
                  ))}
                  <Route path={`${pre}/about`} element={<About />} />
                  <Route path={`${pre}/tools/crawlview`} element={<Tool />} />
                </Route>
              ))}
              {/* Legalele doar în engleză: două traduceri ale unui document care
                  obligă, deviate într-o formulare, te obligă pe amândouă.
                  Versiunea engleză e cea care guvernează. */}
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
            </Route>
            {/* Rutele vechi de servicii. Erau indexate și date clienților,
                deci nu le las să cadă în 404 — duc la blocul de capabilități
                echivalent din landing, care e cel mai apropiat înlocuitor. */}
            <Route path="/web-cloud" element={<Navigate to="/services/web" replace />} />
            <Route path="/desktop" element={<Navigate to="/services/desktop" replace />} />
            <Route path="/automations" element={<Navigate to="/services/automation" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </MotionConfig>
  </RootErrorBoundary>
);

export default App;
